import {
  MouseEvent, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import Typography from '@material-ui/core/Typography';
import FormDatePicker from 'src/components/Control/FormControls/FormDatePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM } from 'src/constants/Form';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import Subtitle from 'src/components/UI/Subtitle';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import { t } from 'src/lib/language';
import InvoiceValidator from 'src/validations/InvoiceValidator';
import InvoiceOrderValidator from 'src/validations/InvoiceOrderValidator';
import {
  InvoiceModel,
  InvoicePayload,
  OrderList,
  OrderModel,
  OrderTaxModel,
  ProductOrderList,
  ProductOrderModel,
} from 'src/models/InvoiceModel';
import { postInvoiceAction, postInvoiceAndSendAction } from 'src/redux/actions/invoiceActions';
import {
  INVOICE_FIELDS,
  INVOICE_ORDER_FIELDS,
  INVOICE_PAYMENT_STATUS,
  INVOICE_STATE,
} from 'src/constants/Invoices';
import {
  getAccountsAction,
  getDepartmentsAction,
  getProgramsAction,
} from 'src/redux/actions/dataActions';
import { getProductListAction } from 'src/redux/actions/productAction';
import { ProductModel } from 'src/models/ProductModel';
import ContactSelector from 'src/components/Control/ContactSelector';
import { AUTOCOMPLETE_ID } from 'src/constants/Autocomplete';
import { getContactAction } from 'src/redux/actions/contactActions';
import { ContactPayload } from 'src/models/ContactModel';
import { getContactInfoForInvoice } from 'src/lib/ContactHelper';
import MetadataSelector, { MetadataType } from 'src/components/Control/MetadataSelector';
import { MODALS_SIZE } from 'src/models/modal';
import OrdersDisplayer from 'src/components/Elements/OrderDisplayer';
import { isEmpty, keyBy } from 'lodash';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { closeModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';
import { Tooltip } from '@material-ui/core';
import ActionButton, { MENU_BUTTON_CATEGORY } from 'src/components/UI/ActionButton';
import SaveIcon from '@material-ui/icons/Save';
import SaveIconOutlined from '@material-ui/icons/SaveOutlined';
import SendIcon from '@material-ui/icons/Send';
import ManageProgramsButton from 'src/components/Control/Buttons/ManageProgramsButton';
import ManageAccountsButton from 'src/components/Control/Buttons/ManageAccountsButton';
import ManageProductsButton from 'src/components/Control/Buttons/ManageProductsButton';
import ManageDepartmentsButton
  from 'src/components/Control/Buttons/ManageDepartmentsButton';
import OrganisationBankAccountSelector
  from 'src/components/Control/OrganisationBankAccountSelector';
import TextField from '@material-ui/core/TextField';
import GatewaySelector from 'src/components/Control/GatewaySelector';
import AddInvoiceContactButton
  from 'src/components/Control/Buttons/AddInvoiceContactButton';
import { ProcessingType } from 'src/models/GatewayModel';
import Form from 'src/components/UI/Form';
import Scroller from 'src/components/UI/Scroller';
import Header from 'src/components/UI/Header';
import Row from 'src/components/UI/Row';
import LargeColumn from 'src/components/UI/LargeColumn';
import Col2 from 'src/components/UI/Col2';
import Amount from 'src/components/UI/Amount';
import Title from 'src/components/UI/Title';
import Button from '@material-ui/core/Button';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone-all';
import FundraisingSelector from 'src/components/Control/FundraisingSelector';
import AmbassadorSelector from 'src/components/Control/AmbassadorSelector';
import Col4 from 'src/components/UI/Col4';
import { FundraisingLink } from 'src/models/FundraisingModel';
import { FundraisingLinkValidator } from 'src/validations/FundraisingTransactionValidator';

type Fields = INVOICE_ORDER_FIELDS | INVOICE_FIELDS;

const AddInvoiceForm = () => {
  const dispatch = useAppDispatch();
  const modalPayload =
    useSelector((state: Store) => {
      if (state.modals[MODALS.addInvoice]) {
        return state.modals[MODALS.addInvoice].payload as InvoiceModel;
      }
      return null;
    });

  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const taxes = useSelector((state: Store) => {
    if (!state.taxes) return {};
    const result = {};
    state.taxes.forEach((tax) => {
      if (tax.id) result[tax.id] = tax;
    });
    return result;
  });
  const countries = useSelector(
    (state: Store) => keyBy(state.data.countries ?? [], 'id'),
  );
  const currentContactId = useSelector(
    (state: Store) => state.currentEntity[ENTITIES.contacts] || 0,
  );
  const [contactId, setContactId] = useState(currentContactId);
  const { contact: contactInfo } = useSelector(
    (state: Store) => {
      if (!!state.contacts[contactId]) {
        return state.contacts[contactId] as ContactPayload;
      }
      return {} as ContactPayload;
    },
  );

  const [invoiceState, setInvoiceState] = useState({
    sameAsBilling: true,
    issue_tax_receipt: false,
    issued_date: moment(),
    due_date: moment(),
    bank_account_id: 0,
    payment_status: INVOICE_PAYMENT_STATUS.not_paid,
    state: INVOICE_STATE.draft,
  } as InvoiceModel);

  const [orderState, setOrderState] = useState([{
    product_id: 0,
    paid: 0,
    quantity: 1,
    deductible: 0,
    discount: 0,
    taxes: {} as OrderTaxModel,
  }] as ProductOrderList);

  const [fundraisingState, setFundraisingState] = useState({} as FundraisingLink);

  const [showInstallment, onToggleInstallment] = useState(false);

  useEffect(() => {
    const modalData = { ...modalPayload };
    if (modalData?.id) {
      delete modalData.id;
    }
    dispatch(setError({ [FORM.add_invoice]: {} }));
    dispatch(getDepartmentsAction());
    dispatch(getAccountsAction());
    dispatch(getProgramsAction());
    dispatch(getProductListAction());
    setInvoiceState({
      ...invoiceState,
      ...modalData,
    });
    if (modalData.order) {
      setOrderState(modalData.order);
    }
  }, []);

  useEffect(() => {
    if (!contactInfo) return;
    const contactInfoForInvoice = getContactInfoForInvoice(contactInfo);
    setInvoiceState({
      ...invoiceState,
      ...contactInfoForInvoice,
      issue_tax_receipt: !!contactInfoForInvoice.issue_tax_receipt,
      contact_salutation_id: (contactInfo.salutation_id === 0)
        ? null
        : contactInfo.salutation_id,
    } as InvoiceModel);
  }, [contactInfo]);

  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'CAD',
  });

  const processing = () => false;

  const setShippingFromBilling = () => ({
    ...invoiceState,
    shipping_address: invoiceState.billing_address,
    shipping_suite: invoiceState.billing_suite,
    shipping_city: invoiceState.billing_city,
    shipping_state: invoiceState.billing_state,
    shipping_country_id: invoiceState.billing_country_id,
    shipping_zip_code: invoiceState.billing_zip_code,
  });

  const formatOrdersForValidation = (orders: any) => {
    const result = {};
    orders.forEach((order: any, index: number) => {
      result[`deductible_${index}`] = order.deductible;
      result[`discount_${index}`] = order.discount;
      result[`note_${index}`] = order.note;
      result[`paid_${index}`] = order.paid;
      result[`product_id_${index}`] = order.product_id;
      result[`quantity_${index}`] = order.quantity;
      result[`taxes_${index}`] = order.taxes;
    });
    return result;
  };

  const getTaxesFromProduct = (product: ProductModel) => {
    const taxesAmount = {} as OrderTaxModel;
    if (!product.taxes) return taxesAmount;
    product.taxes.forEach((taxId: number) => {
      taxesAmount[taxId] = 0;
      if (taxes[taxId]) {
        const price = Number(product.price ?? 0);
        const discount = Number(product.discount ?? 0);
        taxesAmount[taxId] = taxes[taxId].rate * (price - discount);
      }
    });
    return taxesAmount;
  };

  const getTaxItem = (id: number) => taxes[id];

  const getInvoiceTaxes = (orderList: ProductOrderList) => {
    const invoicesTaxes: OrderTaxModel = {};
    orderList.forEach((product:ProductOrderModel) => {
      if (product.taxes) {
        Object.keys(product.taxes).forEach((taxId) => {
          if (!taxes[taxId]) return;
          if (!invoicesTaxes[taxId]) invoicesTaxes[taxId] = 0;
          const paid = Number(product.paid ?? 0);
          const discount = Number(product.discount ?? 0);
          const quantity = Number(product.quantity ?? 0);
          invoicesTaxes[taxId] += (quantity * (paid - discount)) * Number(taxes[taxId].rate);
        });
      }
    });
    return invoicesTaxes;
  };

  const calculatedSubtotal = useMemo(() => {
    let subtotal = 0;
    orderState.forEach((order) => {
      subtotal += ((order.quantity ?? 0) * ((order.paid ?? 0) - (order.discount ?? 0)));
    });
    return subtotal;
  }, [orderState]);

  const calculatedTaxes = useMemo(() => {
    const taxesObject = getInvoiceTaxes(orderState);
    let totalTaxes = 0;
    Object.keys(taxesObject).forEach((taxId) => {
      totalTaxes += Number(taxesObject[taxId]);
    });
    return totalTaxes;
  }, [orderState, taxes]);

  const calculatedTotal = useMemo(() => (
    calculatedSubtotal + calculatedTaxes),
  [calculatedSubtotal, calculatedTaxes]);

  const updateTotals = () => {
    setInvoiceState({
      ...invoiceState,
      total: calculatedTotal,
      subtotal: calculatedSubtotal,
    });
  };

  const updateInvoiceTaxes = (orderList: ProductOrderList) => {
    const invoicesTaxes: OrderTaxModel = {};
    orderList.forEach((product:ProductOrderModel) => {
      if (product.taxes) {
        Object.keys(product.taxes).forEach((taxId) => {
          if (!taxes[taxId]) return;
          const paid = Number(product.paid ?? 0);
          const discount = Number(product.discount ?? 0);
          const quantity = Number(product.quantity ?? 0);
          invoicesTaxes[taxId] += (quantity * paid - discount) * taxes[taxId].rate;
        });
      }
    });
    setInvoiceState({
      ...invoiceState,
      taxes: invoicesTaxes,
    });
  };

  const getTaxesForOrder = (order: ProductOrderModel):string => JSON.stringify(order.taxes);

  const denormalizeOrders = ():OrderList => ({
    ...orderState.map((order: ProductOrderModel) => ({
      ...order,
      deductible: (order.deductible ?? 0) / 100,
      taxes: getTaxesForOrder(order),
    } as OrderModel)),
  });

  const onFieldChange = useCallback((value: Value, field: string) => {
    setInvoiceState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const onFundraisingFieldChange = useCallback((value: Value, field: string) => {
    setFundraisingState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const onInstallmentChange = useCallback((installmentNumber: string) => {
    onFieldChange(parseInt(installmentNumber, 10), 'installments');
  }, []);

  const onContactChange = useCallback((id: number, field: Fields) => {
    onFieldChange(id, field);
    setContactId(id);
    dispatch(getContactAction(id, FORM.add_invoice));
  }, [onFieldChange, dispatch]);

  const onAddProductOrder = useCallback(() => {
    setOrderState((prevOrderState) => [
      ...prevOrderState,
      {
        product_id: 0,
        paid: 0,
        quantity: 1,
        deductible: 0,
      },
    ]);
  }, []);

  const onDeleteProduct = useCallback((orderIndex: number) => {
    const productOrderList = [...orderState];
    productOrderList.splice(orderIndex, 1);
    setOrderState(productOrderList);
  }, []);

  const onUpdateProduct = useCallback((value: Value, field: string, index: number) => {
    setOrderState((prevOrderState) => {
      const orderList = [...prevOrderState];
      orderList[index][field] = value;
      // Also update invoice taxes and totals here or in useEffect that watches orderState
      setInvoiceState((prevInvoiceState) => ({
        ...prevInvoiceState,
        taxes: getInvoiceTaxes(orderList),
      }));
      updateTotals(); // Ensure updateTotals uses functional update too if needed
      return orderList;
    });
  }, [updateTotals]);

  const onProductChange = useCallback((product?: ProductModel, index?: number) => {
    const productOrderList = [...orderState] as ProductOrderList;
    if (index !== undefined) {
      if (product === undefined) {
        productOrderList[index][INVOICE_ORDER_FIELDS.product_id] = 0;
        productOrderList[index][INVOICE_ORDER_FIELDS.paid] = 0;
        productOrderList[index][INVOICE_ORDER_FIELDS.discount] = 0;
        productOrderList[index][INVOICE_ORDER_FIELDS.deductible] = 0;
        productOrderList[index][INVOICE_ORDER_FIELDS.taxes] = {} as OrderTaxModel;
        setOrderState(productOrderList);
        updateInvoiceTaxes(productOrderList);
        updateTotals();
        return;
      }
      productOrderList[index][INVOICE_ORDER_FIELDS.product_id] = product?.id || 0;
      productOrderList[index][INVOICE_ORDER_FIELDS.paid] = product?.price || 0;
      productOrderList[index][INVOICE_ORDER_FIELDS.discount] = product?.discount || 0;
      productOrderList[index][INVOICE_ORDER_FIELDS.deductible] = (product?.deductible || 0) * 100;
      productOrderList[index][INVOICE_ORDER_FIELDS.taxes] = getTaxesFromProduct(product);
      setOrderState(productOrderList);
      setInvoiceState({
        ...invoiceState,
        title: product[`name_${lang}`],
        program_id: product.program_id,
        account_id: product.account_id,
        department_id: product.department_id,
        bank_account_id: product.bank_account_id,
        payment_gateway_cc_id: product.payment_gateway_cc_id,
        payment_gateway_eft_id: product.payment_gateway_eft_id,
        taxes: getInvoiceTaxes(productOrderList),
      });
    }
  }, [lang, updateInvoiceTaxes, updateTotals]);

  const handleClose = () => {
    dispatch(closeModal({ modal: MODALS.addInvoice }));
  };

  const handleSubmit = (
    event: MouseEvent<HTMLElement>,
    state: INVOICE_STATE,
    close:boolean = false,
  ) => {
    event.preventDefault();
    dispatch(setError({ [FORM.add_invoice]: {} }));
    try {
      InvoiceValidator(lang).validateSync({ ...invoiceState }, { abortEarly: false });
      InvoiceOrderValidator(orderState, lang).validateSync(
        formatOrdersForValidation(orderState),
        { abortEarly: false },
      );
      FundraisingLinkValidator().validateSync(fundraisingState, { abortEarly: false });
    } catch (validationErrors: any) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: t(lang, 'forms.contact.validation_errors'),
      }));
      const errorBag = {} as ErrorBag;
      validationErrors.inner.forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
      dispatch(setError({ [FORM.add_invoice]: errorBag }));
      return false;
    }

    let invoice = invoiceState;

    if (invoiceState.sameAsBilling) {
      invoice = setShippingFromBilling();
    }

    const payload = {
      invoice: {
        ...invoice,
        state,
        installment: {
          number: invoice.installments ?? 0,
        },
      },
      event: fundraisingState ?? undefined,
      taxes: getInvoiceTaxes(orderState),
      order: denormalizeOrders(),
    } as InvoicePayload;

    dispatch(postInvoiceAction(payload, close));
    return true;
  };

  const handleSaveAndSend = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    dispatch(setError({ [FORM.add_invoice]: {} }));
    try {
      InvoiceValidator(lang).validateSync({ ...invoiceState }, { abortEarly: false });
      InvoiceOrderValidator(orderState, lang).validateSync(
        formatOrdersForValidation(orderState),
        { abortEarly: false },
      );
      FundraisingLinkValidator().validateSync(fundraisingState, { abortEarly: false });
    } catch (validationErrors: any) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: t(lang, 'forms.contact.validation_errors'),
      }));
      const errorBag = {} as ErrorBag;
      validationErrors.inner.forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
      dispatch(setError({ [FORM.add_invoice]: errorBag }));
      return;
    }

    let invoice = invoiceState;

    if (invoiceState.sameAsBilling) {
      invoice = setShippingFromBilling();
    }

    const payload = {
      invoice: {
        ...invoice,
        state: INVOICE_STATE.final,
        installment: {
          number: invoice.installments ?? 0,
        },
      },
      event: fundraisingState ?? undefined,
      taxes: getInvoiceTaxes(orderState),
      order: denormalizeOrders(),
    } as InvoicePayload;

    dispatch(postInvoiceAndSendAction(payload));
  };

  const getTaxPercentage = (rate:number) => `${Math.ceil(rate * 100000) / 1000}%`;

  const renderTaxes = () => {
    const taxesObject = getInvoiceTaxes(orderState);
    return (
      <>
        { Object.keys(taxesObject).map((taxId: string, index) => {
          const tax = getTaxItem(Number(taxId));
          const abbreviation = tax[`abbreviation_${lang}`];
          return (
            <div
              key={`tax-item-${index}`}
              style={{ textAlign: 'right' }}>
              <span id={`tax-item-${abbreviation}`}>
                {tax[`name_${lang}`]} ({getTaxPercentage(tax.rate)}):&nbsp;
              </span>
              <Amount>{formatter.format(taxesObject[taxId])}</Amount>
            </div>
          );
        }) }
      </>
    );
  };

  const renderContactDetails = () => {
    if (isEmpty(contactInfo)) return '';
    return (
      <div style={{ marginTop: '16px' }}>
        <Row style={{ marginBottom: '8px' }}>
          <Col2>
            <b>Billing Address:</b>
            <div> { contactInfo?.billing_address || '' } </div>
            <div> { contactInfo?.billing_suite || '' }</div>
            <div> { contactInfo?.billing_city || '' }</div>
            <div> { contactInfo?.billing_state || '' }</div>
            <div> { contactInfo?.billing_zip_code || '' }</div>
            <div>{ countries[contactInfo.billing_country_id ?? 0]?.name_en || '' }</div>
          </Col2>
          <Col2>
            <b>Shipping Address:</b>
            <div> { contactInfo?.shipping_address || '' } </div>
            <div> { contactInfo?.shipping_suite || '' } </div>
            <div> { contactInfo?.shipping_city || '' } </div>
            <div> { contactInfo?.shipping_state || '' } </div>
            <div> { contactInfo?.shipping_zip_code || '' }</div>
            <div> { countries[contactInfo?.shipping_country_id ?? 0]?.name_en || '' }</div>
          </Col2>
        </Row>
        <Col2>
          <div> <b>Tel:</b> { contactInfo?.phone_mobile || '' } (Mobile) </div>
          <div> <b>Email:</b> { contactInfo?.email || '' } </div>
        </Col2>
      </div>
    );
  };

  return (
    <>
      <Header height="86px">
        <Row>
          <Title>{t(lang, 'forms.invoices.create_invoice')}</Title>
          <Button
            style={{ marginLeft: 'auto' }}
            variant="contained"
            color="primary"
            size="small"
            onClick={handleClose}>
            {t(lang, 'misc.close')}
          </Button>
        </Row>
        <Row>
          <Tooltip
            title={t(lang, 'menus.save_as_draft')}
            placement="top-start">
            <span>
              <ActionButton
                id="invoice-save-draft"
                disabled={processing()}
                category={MENU_BUTTON_CATEGORY.action}
                onClick={(e) => handleSubmit(e, INVOICE_STATE.draft)}>
                <SaveIconOutlined />
              </ActionButton>
            </span>
          </Tooltip>
          <Tooltip
            title={t(lang, 'menus.save_as_final')}
            placement="top-start">
            <span>
              <ActionButton
                id="invoice-save-final"
                disabled={processing()}
                category={MENU_BUTTON_CATEGORY.action}
                onClick={(e) => handleSubmit(e, INVOICE_STATE.final)}>
                <SaveIcon />
              </ActionButton>
            </span>
          </Tooltip>
          <Tooltip
            title={t(lang, 'menus.save_and_send')}
            placement="top-start">
            <span>
              <ActionButton
                id="invoice-save-and-send"
                disabled={processing()}
                category={MENU_BUTTON_CATEGORY.action}
                onClick={handleSaveAndSend}>
                <SendIcon />
              </ActionButton>
            </span>
          </Tooltip>
          <ManageAccountsButton />
          <ManageDepartmentsButton />
          <ManageProgramsButton />
          <ManageProductsButton />
          <AddInvoiceContactButton />
        </Row>
      </Header>
      <Scroller style={{ paddingRight: '16px' }}>
        <Form maxWidth={MODALS_SIZE.large}>
          <LargeColumn>
            <Subtitle noMargin style={{ marginBottom: '8px' }}>
              {t(lang, 'forms.invoices.invoice_informations')}
            </Subtitle>
            <Row height="72px">
              <FormTextField
                form={FORM.add_invoice}
                label={t(lang, 'forms.invoices.title')}
                name={INVOICE_FIELDS.title}
                onChange={onFieldChange}
                required
                value={invoiceState.title || ''} />
            </Row>
            <Row height="72px">
              <Col2 minHeight="52px">
                <FormDatePicker
                  form={FORM.add_invoice}
                  required
                  label={t(lang, 'forms.invoices.due_date')}
                  name={INVOICE_FIELDS.due_date}
                  onChange={
                  (value: MaterialUiPickersDate) => onFieldChange(
                    moment(value).format('YYYY-MM-DD'),
                    INVOICE_FIELDS.due_date,
                  )
                }
                  value={moment(invoiceState.due_date) || moment()} />
              </Col2>
              <Col2 minHeight="52px">
                <FormDatePicker
                  form={FORM.add_invoice}
                  required
                  label={t(lang, 'forms.invoices.issued_date')}
                  name={INVOICE_FIELDS.issued_date}
                  onChange={
                  (value: MaterialUiPickersDate) => onFieldChange(
                    moment(value).format('YYYY-MM-DD'),
                    INVOICE_FIELDS.issued_date,
                  )
                }
                  value={moment(invoiceState.issued_date) || moment()} />
              </Col2>
            </Row>
          </LargeColumn>
          <Subtitle style={{ marginBottom: '24px' }}>
            {t(lang, 'forms.invoices.contacts')}
          </Subtitle>
          <Row height="72px" style={{ marginTop: '16px' }}>
            <Col2 minHeight="52px">
              <ContactSelector
                id="contact-selector"
                autocompleteId={AUTOCOMPLETE_ID.add_invoice_contact}
                onChange={(id:number) => onContactChange(id, INVOICE_FIELDS.contact_id)}
                label="Contact"
                form={FORM.add_invoice}
                name={INVOICE_FIELDS.contact_id}
                contactId={invoiceState.contact_id}
                hideEdit={!invoiceState.contact_id} />
            </Col2>
            <Col2 minHeight="52px">
              <ContactSelector
                id="seller-selector"
                autocompleteId={AUTOCOMPLETE_ID.add_invoice_seller}
                onChange={(id:number) => onFieldChange(id, INVOICE_FIELDS.seller_id)}
                label="Seller"
                form={FORM.add_invoice}
                name={INVOICE_FIELDS.seller_id}
                contactId={invoiceState.seller_id}
                hideEdit={!invoiceState.seller_id} />
            </Col2>
          </Row>
          <Row height="72px" style={{ marginTop: '16px' }}>
            <Col2 minHeight="52px">
              &nbsp;
            </Col2>
            <Col2 minHeight="52px">
              <ContactSelector
                id="payer-selector"
                autocompleteId={AUTOCOMPLETE_ID.add_invoice_payer}
                onChange={(id:number) => onFieldChange(id, INVOICE_FIELDS.contact_payer_id)}
                label="Payer"
                form={FORM.add_invoice}
                name={INVOICE_FIELDS.contact_payer_id}
                contactId={invoiceState.contact_payer_id}
                hideEdit={!invoiceState.contact_payer_id} />
            </Col2>
          </Row>
          {renderContactDetails()}
          <Subtitle style={{ marginBottom: '16px' }}>{t(lang, 'forms.invoices.orders')}</Subtitle>
          <OrdersDisplayer
            form={FORM.add_invoice}
            onUpdateProduct={onUpdateProduct}
            orders={orderState}
            onDeleteProduct={onDeleteProduct}
            onAddProduct={onAddProductOrder}
            onProductChange={onProductChange} />
          <Row>
            <span style={{ marginRight: '0px', marginLeft: 'auto' }}>
              {t(lang, 'forms.invoices.subtotal')}:&nbsp;
              <Amount id="invoice-subtotal">{formatter.format(calculatedSubtotal)}</Amount>
            </span>
          </Row>
          <Row>
            <span style={{ marginRight: '0px', marginLeft: 'auto' }}>
              <i>{renderTaxes()}</i>
            </span>
          </Row>
          <Row>
            <span style={{ marginRight: '0px', marginLeft: 'auto' }}>
              <b>
                Total:&nbsp;
                <Amount id="invoice-total">{formatter.format(calculatedTotal)}</Amount>
              </b>
            </span>
          </Row>
          <Subtitle style={{ marginBottom: '8px' }}>Payment</Subtitle>
          <div>
            <FormCheckbox
              name={INVOICE_FIELDS.issue_tax_receipt}
              label={t(lang, 'forms.invoices.issue_tax_receipt')}
              checked={invoiceState.issue_tax_receipt}
              onChange={onFieldChange} />
          </div>
          <Row>
            <FormCheckbox
              name="allow-installment"
              label={t(lang, 'forms.invoices.allow_installments')}
              checked={showInstallment}
              onChange={onToggleInstallment} />
          </Row>
          { showInstallment && (
            <Row>
              <Typography
                variant="body1"
                style={{ color: 'rgb(0, 0, 0, .75)' }}>
                {t(lang, 'forms.invoices.number_of_installments')}
              </Typography>
              <TextField
                style={{ width: '35px' }}
                name="installments"
                type="number"
                onChange={(e) => onInstallmentChange(e.target.value as string)}
                value={invoiceState.installments} />
            </Row>
          )}
          <Row height="72px" style={{ marginTop: '16px' }}>
            <Col2 minHeight="52px" style={{ marginTop: '-26px' }}>
              <GatewaySelector
                id="invoice-gateway-eft"
                form={FORM.add_invoice}
                name="payment_gateway_eft_id"
                onChange={(value) => onFieldChange(value, 'payment_gateway_eft_id')}
                value={invoiceState.payment_gateway_eft_id || 0}
                type={ProcessingType.eft}
                noMarginTop />
            </Col2>
            <Col2 minHeight="52px" style={{ marginTop: '-26px' }}>
              <GatewaySelector
                id="invoice-gateway-cc"
                form={FORM.add_invoice}
                name="payment_gateway_cc_id"
                onChange={(value) => onFieldChange(value, 'payment_gateway_cc_id')}
                value={invoiceState.payment_gateway_cc_id || 0}
                type={ProcessingType.cc}
                noMarginTop />
            </Col2>
          </Row>
          <Row style={{ marginTop: '-16px' }}>
            <Col2 minHeight="52px">
              <OrganisationBankAccountSelector
                value={invoiceState.bank_account_id || undefined}
                onChange={(id:number) => onFieldChange(id, INVOICE_FIELDS.bank_account_id)}
                form={FORM.add_invoice}
                name="bank_account_id" />
            </Col2>
          </Row>
          <Subtitle style={{ marginBottom: '8px' }}>Fundraising</Subtitle>
          <Row height="auto">
            <FormCheckbox
              label="Link this invoice to a fundraising"
              name="is_linked"
              onChange={onFundraisingFieldChange}
              checked={!!fundraisingState.is_linked} />
          </Row>
          { fundraisingState.is_linked && (
          <>
            <Row height="auto">
              <FundraisingSelector
                form={FORM.add_invoice}
                label="Fundraising"
                name="event_id"
                value={fundraisingState.event_id}
                onChange={(id) => onFundraisingFieldChange(id, 'event_id')} />
              <AmbassadorSelector
                form={FORM.add_invoice}
                fundraisingId={fundraisingState.event_id}
                label="Ambassador"
                value={fundraisingState.ambassador_id}
                name="ambassador_id"
                onChange={(id) => onFundraisingFieldChange(id, 'ambassador_id')} />
            </Row>
            <Row>
              <Col4>
                <FormTextField
                  form={FORM.add_invoice}
                  label="Amount multiplier"
                  name="amount_multiplier"
                  onChange={onFundraisingFieldChange}
                  value={fundraisingState.amount_multiplier} />
              </Col4>
            </Row>
            <Row height="auto">
              <Col2>
                <FormTextField
                  form={FORM.add_invoice}
                  label="Donor name displayed"
                  name="donor_name_displayed"
                  onChange={onFundraisingFieldChange}
                  value={fundraisingState.donor_name_displayed} />
              </Col2>
              <Col2>
                <FormCheckbox
                  label="Anonymous Donor"
                  name="is_anonymous"
                  onChange={onFundraisingFieldChange}
                  checked={!!fundraisingState.is_anonymous}
                  disabled={processing()} />
              </Col2>
            </Row>
            <Row height="auto">
              <FormTextField
                form={FORM.add_invoice}
                label="Donor Comment"
                name="donor_comment"
                onChange={onFundraisingFieldChange}
                multiline
                minRows={3}
                maxRows={5}
                value={fundraisingState.donor_comment} />
            </Row>
            <Row height="auto">
              <Col2>
                <FormTextField
                  form={FORM.add_invoice}
                  label="Source reference"
                  name="source_reference"
                  onChange={onFundraisingFieldChange}
                  value={fundraisingState.source_reference} />
              </Col2>
              <Col2>
                <FormTextField
                  form={FORM.add_invoice}
                  label="Source reference URL"
                  name="source_reference_url"
                  onChange={onFundraisingFieldChange}
                  value={fundraisingState.source_reference_url} />
              </Col2>
            </Row>
          </>
          )}
          <Subtitle style={{ marginBottom: '8px' }}>Meta Data</Subtitle>
          <Row height="64px" style={{ marginTop: '16px' }}>
            <Col2 minHeight="52px">
              <MetadataSelector
                id="program-selector"
                type={MetadataType.program}
                onChange={(id) => onFieldChange(id, INVOICE_FIELDS.program_id)}
                form={FORM.add_invoice}
                name={INVOICE_FIELDS.program_id}
                value={invoiceState.program_id || undefined}
                noMarginTop />
            </Col2>
            <Col2 minHeight="52px">
              <MetadataSelector
                id="department-selector"
                type={MetadataType.department}
                onChange={(id) => onFieldChange(id, INVOICE_FIELDS.department_id)}
                form={FORM.add_invoice}
                name={INVOICE_FIELDS.department_id}
                value={invoiceState.department_id || undefined}
                noMarginTop />
            </Col2>
          </Row>
          <Row height="64px" style={{ marginTop: '16px' }}>
            <Col2 minHeight="52px">
              <MetadataSelector
                id="account-selector"
                type={MetadataType.account}
                onChange={(id) => onFieldChange(id, INVOICE_FIELDS.account_id)}
                form={FORM.add_invoice}
                name={INVOICE_FIELDS.account_id}
                value={invoiceState.account_id || undefined}
                noMarginTop />
            </Col2>
          </Row>
          <Row>
            <FormTextField
              id="invoice-note"
              form={FORM.add_invoice}
              label={t(lang, 'forms.invoices.note')}
              multiline
              minRows="4"
              name={INVOICE_FIELDS.note}
              onChange={onFieldChange}
              value={invoiceState.note || ''} />
          </Row>
        </Form>
      </Scroller>
    </>
  );
};

export default AddInvoiceForm;
